import { FC, ReactNode, memo } from 'react';
import { PunchupTile } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { REVENUE_HELP_COPY } from 'features/common/utils/revenueHelpCopy';
import { HighlightCardProps } from './types';
import { PunchupPercentPieChart } from './PunchupPercentPieChart';
import {
	SHOW_RATE,
	TOTAL_PAYOUT,
	TOTAL_ATTENDEES,
	TICKETS_COMPED,
	LOTTERY,
	EVENT_SCANS,
} from 'features/common/components/HighlightsCollection/constants';

export const convertToNum = (stat: string | number) => {
	if (typeof stat === 'string') return parseFloat(stat);
	return stat;
};

const helpButtonByProperty = (property: string): ReactNode => {
	switch (property) {
		case TOTAL_PAYOUT:
			// Same figure as the Analytics "Revenue" KPI tile → identical copy (single source).
			return (
				<PunchupHelpButton
					mainText={REVENUE_HELP_COPY.mainText}
					codeText={REVENUE_HELP_COPY.codeText}
					secondaryText={REVENUE_HELP_COPY.secondaryText}
				/>
			);
		case TOTAL_ATTENDEES:
			return (
				<PunchupHelpButton
					mainText="Full list of ticket holders registered for this event — search by name, view ticket details, question responses, and manage individual attendees."
					secondaryText="Navigates to the Attendee List."
				/>
			);
		case TICKETS_COMPED:
			return (
				<PunchupHelpButton
					mainText="Issue complimentary tickets directly to guests — specify recipient details and ticket type without payment."
					secondaryText="Navigates to Comped Tickets."
				/>
			);
		case LOTTERY:
			return (
				<PunchupHelpButton
					mainText="View all lottery entries and manage draws — see who entered, run the selection process, and notify winners."
					secondaryText="Navigates to the Lottery page."
				/>
			);
		case EVENT_SCANS:
			return (
				<PunchupHelpButton
					mainText="A record of every ticket scanned at the door — each scan's time, attendee, ticket type, and seat."
					secondaryText="Navigates to the Scanned Tickets page."
				/>
			);
		default:
			return null;
	}
};

/**
 * Renders a single stat tile on the Dashboard highlights row.
 * Delegates all visual structure to PunchupTile.
 *
 * No icons: this strip is `variant="highlight"`, which is an iconless treatment (PUN-4943) —
 * the eyebrow label and the orange entry line carry the tile. `BoxProps.icon` is therefore
 * not read here; it still serves the legacy (non-green) highlight card.
 *
 * Entry line: `entryLabel` is passed straight through to the tile, which anchors it to the
 * tile's bottom edge. The strings are per-tile DATA and come in with the box — this component
 * never invents them.
 *
 * Help buttons: each known property maps to a PunchupHelpButton describing
 * what the tile navigates to. The mapping lives here so the data layer
 * (BoxProps, constants) stays free of ReactNode concerns.
 */
export const PunchupHighlightCard: FC<HighlightCardProps> = memo(
	({ property, stat, cardLabel, buttonLabel, entryLabel, onClick, dataCy, isLoading }: HighlightCardProps) => {
		/* SHOW_RATE needs the raw float for the pie chart (convertToNum); every other tile rounds the
	   number to a readable integer for display. Two coercions, two deliberate treatments. */
		const formattedStat = typeof stat === 'number' ? Math.round(stat) : stat;

		if (property === SHOW_RATE) {
			return (
				<PunchupTile
					variant="highlight"
					title={cardLabel}
					dataCy={dataCy}
					isLoading={isLoading}
					entryLabel={entryLabel}
					value={
						<PunchupPercentPieChart
							color="var(--punchup-color-primary)"
							sizes={{ fontSize: 24, iconSize: 100 }}
							rawPercentage={convertToNum(stat)}
						/>
					}
				/>
			);
		}

		return (
			<PunchupTile
				variant="highlight"
				title={cardLabel}
				value={formattedStat}
				descriptions={buttonLabel ? [buttonLabel] : undefined}
				entryLabel={entryLabel}
				onClick={onClick}
				helpButton={helpButtonByProperty(property)}
				dataCy={dataCy}
				isLoading={isLoading}
			/>
		);
	}
);

PunchupHighlightCard.displayName = 'PunchupHighlightCard';
